import { useEffect, useState } from 'react';

/**
 * Resolves a deferred loader promise into a plain nullable value: `null`
 * until the promise settles, then the resolved value. Unlike `<Await>` this
 * doesn't suspend, so several promises can drive independent loading states
 * inside one stateful component (e.g. per-section skeletons in a dialog).
 *
 * Semantics:
 * - When the promise identity changes (loader revalidation), the previous
 *   value stays visible until the new one resolves — stale-while-revalidate,
 *   so the UI never falls back to a skeleton once it has data.
 * - A rejection is rethrown into render, so the route error boundary handles
 *   it exactly like a thrown loader error.
 * - During SSR this returns `null` (the value fills in on the client), which
 *   is the intended skeleton-first behavior for deferred data.
 */
export function useAwaited<T>(promise: Promise<T>): T | null {
  const [resolved, setResolved] = useState<{ value: T } | null>(null);
  const [error, setError] = useState<unknown>(null);
  if (error !== null) throw error;

  useEffect(() => {
    let active = true;
    promise.then(
      (value) => {
        if (active) setResolved({ value });
      },
      (e: unknown) => {
        if (active) setError(e ?? new Error('useAwaited: promise rejected'));
      }
    );
    return () => {
      active = false;
    };
  }, [promise]);

  return resolved ? resolved.value : null;
}
